import React, { Fragment } from "react";
import { StepRenderProps } from "../../../interfaces/FlowInterfaces";
import { createStyles, makeStyles, TableCell, Theme, Typography } from "@material-ui/core";
import { observer } from "mobx-react";
import { Alert, AlertTitle } from "@material-ui/lab";
import { VolunteerRegistrationFlowExternalState, ViewVolunteersStepValues } from "../../_interfaces/IAdminVolunteerRegistrationStepTypeValues";
import { IAdminVolunteerRegistrationFlowValues } from "../../_interfaces/IAdminVolunteerRegistrationFlowValues";
import RecordTable from "../../../../../components/Shared/Table/NewRecordTable";
import REMChip from "../../../../../shared/modules/rem-conversion/components/Chip";
import { IVolunteerOverview } from "../../../../../stores/models/VolunteerOverview";
import { ISortableTableHeader, TableHeader } from "../../../../../stores/models/TableHeader";
import { Option } from "../../../../../stores/models/Option";
import clsx from "clsx";
import { StylizedInternalLink } from "../../../../../components/Navigation/Components";
import CheckboxTableCell from "../../../../../components/Shared/Table/CheckboxTableCell";
import { useStandardStyles } from "../../../../../shared/general/hooks/useStandardStyles";
import { DialogState } from "../../../../../stores/models/DialogState";
import { Shift } from "../../../../../stores/models/Shift";
import { useViewVolunteersController } from "../../hooks/useViewVolunteersController";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        header: {
            marginBottom: theme.spacing(2),
        },
        section: {
            marginBottom: theme.spacing(2),
        },
        alert: {
            marginBottom: theme.spacing(2),
            boxSizing: 'border-box',
            textAlign: 'left',
            flexGrow: 1
        },
        noWrap: {
            display: 'flex',
            flexWrap: 'nowrap',
            margin: '-6px 0px'
        },
        chip: {
            '& .MuiChip-icon, .MuiChip-label': {
                color: theme.palette.darkBackground.main
            },
            '&:not(:last-child)': {
                marginRight: theme.spacing(.5),
            },
            marginBottom: theme.spacing(.5),
        },
        tableChip: {
            backgroundColor: theme.palette.primaryBackground.main,
        },
        link: {
            color: theme.palette.primary.main
        },
        viewButton: {
            marginLeft: theme.spacing(1)
        },
    })
);

const MAX_ROLE_CHIPS_TO_DISPLAY = 2;

const headCells = ([
    { id: 'checkbox', alignment: 'center', disablePadding: true, sortable: false, checkbox: true },
    { id: 'firstName', alignment: 'left', disablePadding: false, label: 'First Name', sortable: true, sortValue: 'firstName' },
    { id: 'lastName', alignment: 'left', disablePadding: false, label: 'Last Name', sortable: true, sortValue: 'lastName' },
    { id: 'status', alignment: 'left', disablePadding: false, label: 'Status', sortable: true, sortValue: 'status' },
    { id: 'roles', alignment: 'left', disablePadding: false, label: 'Roles', sortable: false },
] as ISortableTableHeader<IVolunteerOverview>[]);

const volunteerTableHeaderCells = headCells.map(headCell => new TableHeader(headCell));

const dataCell = (key: keyof IVolunteerOverview) => {
    return (option: Option<IVolunteerOverview>, rowIndex: number, cellIndex: number) => {
        return (
            <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>{option.object[key]}</TableCell>
        );
    }
};

export const ViewVolunteersStep: React.FC<
    StepRenderProps<ViewVolunteersStepValues, IAdminVolunteerRegistrationFlowValues, VolunteerRegistrationFlowExternalState>
> = observer(({
    values,
    update,
    submit,
    canSubmit,
    validation,
    allValues,
    externallyManagedState
}) => {

    /********* React hooks *********/

    const classes = useStyles();
    const baseClasses = useStandardStyles();
    const controller = useViewVolunteersController(
        values,
        allValues,
        externallyManagedState
            ? externallyManagedState
            : { registeredVolunteerIds: [], topLevelShift: new Shift(), dialogState: new DialogState() },
        update,
        submit
    );

    const tableController = controller.stepBoundValues.tableController;

    const onCheckboxClicked = (event: React.MouseEvent<HTMLButtonElement | HTMLTableHeaderCellElement, MouseEvent>, option: Option<IVolunteerOverview>) => {
        event.stopPropagation();
        if (!option.disabled) {
            tableController?.toggleOptionSelection(option);
        }
    }

    /********* Render *********/

    return (
        <Fragment>
            {externallyManagedState?.dialogState && tableController ?
                <Fragment>
                    {/* Error Notification */}
                    {controller.fieldErrors && Object.values(controller.fieldErrors).length > 0
                        ? <Alert severity="error" className={classes.alert}>
                            <AlertTitle>Registration Error</AlertTitle>
                            {controller.fieldErrors.volunteerIds}
                        </Alert>
                        : null
                    }
                    {controller.error
                        ? <Alert severity="error" className={classes.alert}>
                            <AlertTitle>Registration Error</AlertTitle>
                            {controller.error}
                        </Alert>
                        : null
                    }
                    {/* Header */}
                    <div>
                        <Typography variant="h5" className={classes.header}>Selected Volunteers</Typography>
                    </div>
                    {/* Selected Volunteers Count */}
                    {allValues.volunteersTableController?.persistentSelections
                        ? <div className={classes.section}>
                            <Typography className={baseClasses.bold}>Selected Volunteers</Typography>
                            <div className={baseClasses.flex}>
                                <Typography>
                                    {
                                        allValues.volunteersTableController.persistentSelections.options.length === 1
                                            ? `${allValues.volunteersTableController.persistentSelections.options.length} volunteer selected`
                                            : `${allValues.volunteersTableController.persistentSelections.options.length} volunteers selected`
                                    }
                                </Typography>
                                <StylizedInternalLink
                                    onClick={() => {
                                        update({ backClicked: true });
                                        submit({ backClicked: true });
                                    }}
                                    children={<Typography className={clsx(classes.link, classes.viewButton)}>Select More</Typography>}
                                />
                            </div>
                        </div>
                        : null
                    }
                    {/* Volunteer Table */}
                    <RecordTable
                        controller={tableController}
                        tableHeaderCells={volunteerTableHeaderCells}
                        displayFunctions={[
                            (option: Option<IVolunteerOverview>, rowIndex: number, cellIndex: number) => {
                                return (
                                    <CheckboxTableCell
                                        key={`${rowIndex}-${cellIndex}`}
                                        CheckboxProps={{
                                            checked: option.disabled || option.selected,
                                            disabled: option.disabled
                                        }}
                                        onCheckboxClicked={(event) => onCheckboxClicked(event, option)}
                                    />
                                )
                            },
                            dataCell('firstName'),
                            dataCell('lastName'),
                            dataCell('status'),
                            (option: Option<IVolunteerOverview>, rowIndex: number, cellIndex: number) => {
                                return (
                                    <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                        <div className={classes.noWrap}>
                                            {option.object.roles.map((role, index) => {
                                                if (index < MAX_ROLE_CHIPS_TO_DISPLAY) {
                                                    return (
                                                        <REMChip
                                                            label={role.position}
                                                            className={clsx(classes.chip, classes.tableChip)}
                                                            key={`role-chip-${index}`}
                                                        />
                                                    )
                                                } else if (index === MAX_ROLE_CHIPS_TO_DISPLAY) {
                                                    return (
                                                        <REMChip
                                                            label={`+${option.object.roles.length - MAX_ROLE_CHIPS_TO_DISPLAY}`}
                                                            className={clsx(classes.chip, classes.tableChip)}
                                                            key={`role-chip-${index}`}
                                                        />
                                                    )
                                                }
                                            })}
                                        </div>
                                    </TableCell>
                                );
                            }
                        ]}
                        onRowClicked={(option: Option<IVolunteerOverview>, rowIndex: number) => {
                            if (!option.disabled) {
                                tableController.toggleOptionSelection(option);
                            }
                        }}
                    />
                </Fragment >
                : null
            }
        </Fragment>
    );
});